import type * as PIXI from 'pixi.js';
import { ParentMixin } from '../core/parentMixin';
import { UpdateFlowMixin } from '../core/updateFlowMixin';
import type { UpdateFlowMixinSettings } from '../core/updateFlowMixin';
import { ReactiveBase, comparer, observable, reaction, runInAction } from '../core/reactivity';
import type { LoadSettings } from '../core/serializableMixin';
import { SerializableMixin } from '../core/serializableMixin';
import { BooleanAttribute, getAttributesToSerialize, StringAttribute } from '../core/attributes';
import { Config } from '../video-data/config';
import { BoxGraphic } from '../core/scene/graphicsStyle/BoxGraphic';
import { CircleGraphic } from '../core/scene/graphicsStyle/CircleGraphic';
import { BoxBorderGraphic } from '../core/scene/graphicsStyle/BoxBorderGraphic';

import { assertInEnum } from '../validators';
import type { GraphicClassTypes } from '../enums';
import { DynamicStylingCreationLevel, AllGraphicClassTypes, CreationType } from '../enums';
import type { AnimatableContainer } from '../core/dynamicStylingMixin';
import { DynamicAnimationConfiguration } from './DynamicAnimationConfiguration';
import { holdConfigCurveAtRest } from './AnimationCurveManager';
import { DynamicBoxConfigurator } from './DynamicBoxConfigurator';
import { DynamicCircleConfigurator } from './DynamicCircleConfigurator';
import { DynamicBoxBorderConfigurator } from './DynamicBoxBorderConfigurator';

export class DynamicGraphicsConfigurator extends UpdateFlowMixin(
    ParentMixin(SerializableMixin(ReactiveBase)),
) {
    override _name = 'dynamicGraphicsConfigurator';
    override _classType = 'DynamicGraphicsConfigurator';

    @StringAttribute({ choices: Config.ALL_GRAPHICS })
    @observable
    accessor graphic: (typeof Config.ALL_GRAPHICS)[number] = Config.BOX_GRAPHIC;
    @BooleanAttribute({ serialize: false, animatable: false })
    accessor enabled = true;
    @BooleanAttribute({ animatable: false })
    accessor asMask = false;
    @StringAttribute({ choices: Object.values(DynamicStylingCreationLevel) })
    accessor level = DynamicStylingCreationLevel.Object;
    @StringAttribute({ choices: Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL_OPTIONS })
    accessor alignGraphicX = Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.LEFT;
    @StringAttribute({ choices: Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL_OPTIONS })
    accessor alignGraphicY = Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.TOP;

    animation: DynamicAnimationConfiguration[] = [];
    @observable.ref
    accessor shape:
        | DynamicBoxConfigurator
        | DynamicCircleConfigurator
        | DynamicBoxBorderConfigurator = new DynamicBoxConfigurator({ parent: this });
    _maskedContainers: AnimatableContainer[] = [];
    _graphics: (BoxGraphic | CircleGraphic | BoxBorderGraphic)[] = [];
    #destroyed = false;

    get destroyed() {
        return this.#destroyed;
    }

    get #parentAsset() {
        return this.parent as unknown as
            | {
                  getCreationContainersAtLevelForType: (
                      level: DynamicStylingCreationLevel,
                      type: CreationType,
                  ) => AnimatableContainer[];
                  _animationAnchorsToRemove: DynamicAnimationConfiguration['anchorsToRemove'];
                  isMatchCutFrozen: boolean;
                  animationManager: { add: (config: unknown) => void };
              }
            | null
            | undefined;
    }

    // Cache data about the containers so that multiple dynamic
    // graphics don't influence each other
    #containers: AnimatableContainer[] = [];
    #containerProperties: Record<string, unknown>[] = [];
    #containerBounds: PIXI.Rectangle[] = [];

    constructor(settings: UpdateFlowMixinSettings) {
        super(settings);
        this.addReaction(
            reaction(
                () => ({ graphic: this.graphic, shapeGraphicType: this.shape.graphicType }),
                ({ graphic, shapeGraphicType }) => {
                    if (shapeGraphicType === graphic) {
                        return;
                    }
                    this._createShape(graphic);
                    this.requiresUpdate = true;
                },
                { equals: comparer.shallow, name: 'DynamicGraphicsConfigurator.shape' },
            ),
        );
    }

    /**
     * Returns the creation containers for the given level used for dynamic styling. Currently there is just one
     * level: object. It is possible for an asset to only support one level. In that case, an empty
     * array will be returned for the unsupported level.
     * @param type - animation or graphic
     */
    getCreationContainersAtLevelForType(
        value: DynamicStylingCreationLevel,
        type = CreationType.Animation,
    ): any[] {
        assertInEnum(type, CreationType);
        let result: (BoxGraphic | CircleGraphic | BoxBorderGraphic)[] = [];
        switch (value) {
            case DynamicStylingCreationLevel.Object:
                result = this._graphics.filter((graphic) => !graphic.destroyed);
                break;
            default:
                console.warn(`Unsupported container of type ${value}`);
        }
        return result;
    }

    /**
     * @returns A list of `classType` of the graphic shapes that this configurator can create.
     */
    get supportedShapeClassTypes(): readonly string[] {
        return AllGraphicClassTypes;
    }

    /**
     * Returns an array of class types for all current graphics in use
     */
    getCurrentGraphicsClassTypes(): GraphicClassTypes[] {
        return this._graphics
            .filter((graphic) => !graphic.destroyed)
            .map((graphic) => graphic.classType as GraphicClassTypes);
    }

    _createShape(graphic: (typeof Config.ALL_GRAPHICS)[number]) {
        runInAction(() => {
            this.graphic = graphic;
            switch (graphic) {
                case Config.BOX_GRAPHIC:
                    this.shape = new DynamicBoxConfigurator({
                        parent: this,
                    });
                    break;
                case Config.CIRCLE_GRAPHIC:
                    this.shape = new DynamicCircleConfigurator({
                        parent: this,
                    });
                    break;
                case Config.BOX_BORDER_GRAPHIC:
                    this.shape = new DynamicBoxBorderConfigurator({
                        parent: this,
                    });
                    break;
                default:
                    throw Error(`Unsupported graphic type: "${graphic}"`);
            }
        });
    }

    /**
     * Extract the necessary properties from our creation containers to create the graphics.
     * @param containers - list of containers, which will hold the new the graphics
     * @returns - list of properties for each graphic controlled by this configurator
     */
    _getGraphicProperties(containers: AnimatableContainer[]) {
        const shapeProperties = this.shape.save(true, false, false);
        switch (this.level) {
            case DynamicStylingCreationLevel.Object:
                return this._objectLevelProperties(shapeProperties, containers);
        }
    }

    _objectLevelProperties(properties: Record<string, unknown>, containers: AnimatableContainer[]) {
        let inferredProperties = this._getGlobalInferredProperties(properties, containers);
        inferredProperties = this._getLocalInferredProperties(inferredProperties, containers[0]);
        this._getZigZagInferredProperties(inferredProperties);
        return [inferredProperties];
    }

    _buildObjectLevelGraphics(
        properties: Record<string, unknown>[],
        containers: AnimatableContainer[],
    ) {
        this._clearOldGraphics(properties.length);
        const containerBounds = this.#containerBounds[0];
        if (containerBounds.width === 0 || containerBounds.height === 0) {
            // If the container is empty, we don't want to draw any graphics
            return;
        }
        const pixiGraphic = this._getOrCreateGraphic(0, containers[0]);
        this._loadGraphicProperties(pixiGraphic, properties[0]);
        if (this.asMask) {
            this._setMaskOnContainer(pixiGraphic, containers[0]);
        }
        const graphicPosition = this._getGraphicPosition(
            pixiGraphic,
            containers[0],
            this.#containerBounds[0],
        );
        pixiGraphic.x = graphicPosition.x;
        pixiGraphic.y = graphicPosition.y;
    }

    _getOrCreateGraphic(index: number, container: AnimatableContainer) {
        let graphic: BoxGraphic | CircleGraphic | BoxBorderGraphic;
        // Graphics can get destroyed outside of the configurator if its parent gets destroyed
        if (this._graphics[index] && !this._graphics[index].destroyed) {
            graphic = this._graphics[index];
        } else {
            graphic = this._createGraphic();
            this._graphics.push(graphic);
        }
        container.addChildAt(graphic, 0);
        return graphic;
    }

    _createGraphic() {
        switch (this.graphic) {
            case Config.BOX_GRAPHIC:
                return new BoxGraphic({});
            case Config.CIRCLE_GRAPHIC:
                return new CircleGraphic({});
            case Config.BOX_BORDER_GRAPHIC:
                return new BoxBorderGraphic({});
            default:
                throw new Error(`Unrecognized graphic ${this.graphic}`);
        }
    }

    _loadGraphicProperties(
        graphic: BoxGraphic | CircleGraphic | BoxBorderGraphic,
        properties: Record<string, unknown>,
    ) {
        const initAutoUpdate = graphic.autoUpdate;
        graphic.autoUpdate = false;
        graphic.load(properties as never);
        graphic.autoUpdate = initAutoUpdate;
    }

    _getGlobalInferredProperties(
        properties: Record<string, unknown>,
        containers: AnimatableContainer[],
    ) {
        const inferredProperties: any = {};
        Object.keys(properties).forEach((property) => {
            let value = properties[property];
            if (typeof value === 'string') {
                switch (value) {
                    case Config.INFERRED_MAX_WIDTH:
                        value = Math.max(
                            ...containers.map((container) => container.getBounds().width),
                        );
                        break;
                    case Config.INFERRED_MAX_HEIGHT:
                        value = Math.max(
                            ...containers.map((container) => container.getBounds().height),
                        );
                        break;
                    case Config.INFERRED_HEIGHT_GAPLESS:
                        value = Math.max(
                            ...containers.map((container) => container.getBounds().height),
                        );
                        break;
                }
            }
            inferredProperties[property] = value;
        });
        return inferredProperties;
    }

    _getLocalInferredProperties(
        properties: Record<string, unknown>,
        container: AnimatableContainer,
    ) {
        const bounds = container.getBounds();
        const inferredProperties: any = {};
        Object.keys(properties).forEach((property) => {
            let value = properties[property];
            if (typeof value === 'string') {
                switch (value) {
                    case Config.INFERRED_WIDTH:
                        value = bounds.width;
                        break;
                    case Config.INFERRED_HEIGHT:
                        value = bounds.height;
                        break;
                }
            }
            inferredProperties[property] = value;
        });
        return inferredProperties;
    }

    _getZigZagInferredProperties(properties: Record<string, unknown>, index = 0) {
        if (properties.horizontalDirection === Config.ZIG_ZAG_LEFT_RIGHT_DIRECTION) {
            properties.horizontalDirection =
                index % 2 ? Config.RIGHT_ANIMATION_DIRECTION : Config.LEFT_ANIMATION_DIRECTION;
        }
        if (properties.horizontalDirection === Config.ZIG_ZAG_RIGHT_LEFT_DIRECTION) {
            properties.horizontalDirection =
                index % 2 ? Config.LEFT_ANIMATION_DIRECTION : Config.RIGHT_ANIMATION_DIRECTION;
        }
        if (properties.verticalDirection === Config.ZIG_ZAG_TOP_BOTTOM_DIRECTION) {
            properties.verticalDirection =
                index % 2 ? Config.BOTTOM_ANIMATION_DIRECTION : Config.TOP_ANIMATION_DIRECTION;
        }
        if (properties.verticalDirection === Config.ZIG_ZAG_BOTTOM_TOP_DIRECTION) {
            properties.verticalDirection =
                index % 2 ? Config.TOP_ANIMATION_DIRECTION : Config.BOTTOM_ANIMATION_DIRECTION;
        }
    }

    _getGraphicPosition(
        graphic: BoxGraphic | CircleGraphic | BoxBorderGraphic,
        container: AnimatableContainer,
        containerBounds: PIXI.Rectangle,
    ) {
        const { width: containerWidth, height: containerHeight } = containerBounds;
        const { width: graphicWidth, height: graphicHeight } = graphic.getBounds();
        const padding = graphic as Partial<
            Pick<BoxGraphic, 'paddingRight' | 'paddingLeft' | 'paddingTop' | 'paddingBottom'>
        >;

        let x = 0;
        const paddingRight = padding.paddingRight ?? 0;
        const paddingLeft = padding.paddingLeft ?? 0;
        switch (this.alignGraphicX) {
            case Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.LEFT:
                x = -paddingLeft;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.CENTER:
                const widthWithoutPadding = graphicWidth - paddingLeft - paddingRight;
                x = (containerWidth - widthWithoutPadding) / 2 - paddingLeft;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.RIGHT:
                x = containerWidth - (graphicWidth - paddingRight);
                break;
        }

        let y = 0;
        const paddingTop = padding.paddingTop ?? 0;
        const paddingBottom = padding.paddingBottom ?? 0;
        switch (this.alignGraphicY) {
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.TOP:
                y = -paddingTop;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.CENTER:
                const heightWithoutPadding = graphicHeight - paddingTop - paddingBottom;
                y = (containerHeight - heightWithoutPadding) / 2 - paddingTop;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.BOTTOM:
                y = containerHeight - (graphicHeight - paddingBottom);
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.BASELINE:
                y = containerHeight - (graphicHeight - paddingBottom);
                break;
        }

        return { x, y };
    }

    /**
     * Cleans up graphics which should no longer exist because the graphic type changed or
     * the graphic has been destroyed.
     */
    _clearOldGraphics(numExistingGraphics: number) {
        this.clear(numExistingGraphics);
        this._graphics = this._graphics.filter((graphic) => {
            if (graphic.destroyed) {
                return false;
            }
            if (graphic._name !== this.graphic) {
                graphic.destroy();
                return false;
            }
            return true;
        });
    }

    clear(startAt = 0) {
        const graphicsToClear = this._graphics.splice(startAt);
        graphicsToClear.forEach((graphic) => {
            if (!graphic.destroyed) {
                graphic.destroy();
            }
        });
    }

    override destroy() {
        this.#destroyed = true;
        this._removeMasksFromContainers();
        this.clear();
        super.destroy();
    }

    addAnimation(): DynamicAnimationConfiguration {
        const animationConfigurator = new DynamicAnimationConfiguration({
            parent: this,
        });
        // Copy array so that React understands that the animations changed
        this.animation = [...this.animation, animationConfigurator];
        this.requiresUpdate = true;
        return animationConfigurator;
    }

    removeAnimation(index: number) {
        this.animation.splice(index, 1);
        this.requiresUpdate = true;
    }

    override get readonlyProperties() {
        return [];
    }

    override get propertiesToSerialize() {
        return ['animation'].concat(getAttributesToSerialize(this));
    }

    override get editableProperties() {
        return super.editableProperties.concat(['graphic', 'shape']);
    }

    _animationCustomLoad(configData: Record<string, unknown>[]) {
        this.animation.splice(configData.length);
        configData.forEach((data, index) => {
            const animation = this.animation[index] ?? this.addAnimation();
            animation.load(data as Parameters<typeof animation.load>[0]);
        });
    }

    _animationCustomSave() {
        return this.animation.map((animation) => animation.save());
    }

    /**
     * This should be called for all dynamic styles before collectProperties.
     *
     * This removes the graphics from the asset hierarchy so they can be properly
     * removed or updated without influencing each other.
     * For example, we want to remove all color graphics first, otherwise once we do the
     * container calculations (i.e. getBounds) it will include the dynamic graphics as well
     */
    prepareForUpdate() {
        this._removeMasksFromContainers();
        this._unparentGraphics();
    }

    /**
     * This should be called for all dynamic styles before createOrUpdateGraphics.
     *
     * This runs the property calculations, using the containers data without any dynamic styles
     * attached to them. This ensures dynamic styles don't influence each other.
     */
    collectProperties() {
        this.#containers =
            this.#parentAsset?.getCreationContainersAtLevelForType(
                this.level,
                CreationType.Graphic,
            ) || [];
        if (this.#containers.length === 0) {
            this.#containerProperties = [];
            return;
        }
        this.#containerProperties = this._getGraphicProperties(this.#containers) ?? [];
        this.#containerBounds = this.#containers.flat().map((container) => container.getBounds());
    }

    /**
     * After all dynamics styles have called prepareForUpdate and collectProperties, then they
     * can be built (or updated) by calling this method.
     */
    createOrUpdateGraphics() {
        if (this.destroyed) {
            return;
        }
        if (!this.enabled) {
            this.clear();
            return;
        }

        this._updateGraphics();
        this._updateAnimation();
    }

    _removeMasksFromContainers() {
        this._maskedContainers.forEach((container) => {
            container.mask = null;
        });
        this._maskedContainers = [];
    }

    _unparentGraphics() {
        // We silently un-parent and parent graphics during property calculation, this way
        // getBounds calculations will exclude dynamic graphics which are being re-used
        this._graphics.forEach((graphic) => {
            graphic.parent?.removeChild(graphic);
        });
    }

    _setMaskOnContainer(
        graphic: BoxGraphic | CircleGraphic | BoxBorderGraphic,
        container: AnimatableContainer,
    ) {
        container.mask = graphic;
        this._maskedContainers.push(container);
    }

    _updateGraphics() {
        if (this.#containers.length === 0) {
            this.clear();
            return;
        }

        // build graphic(s)
        switch (this.level) {
            case DynamicStylingCreationLevel.Object:
                this._buildObjectLevelGraphics(this.#containerProperties, this.#containers);
                break;
            default:
                throw new Error(`Unrecognized level ${this.level}`);
        }
    }

    _updateAnimation() {
        // build animation
        const parent = this.#parentAsset;
        if (!parent) {
            return;
        }
        this.animation.forEach((configurator) => {
            configurator.anchorsToRemove = parent._animationAnchorsToRemove;
            if (!configurator.enabled) {
                return;
            }
            const animationCurveConfig = configurator.buildConfig();
            if (parent.isMatchCutFrozen) {
                holdConfigCurveAtRest(animationCurveConfig);
            }
            parent.animationManager.add(animationCurveConfig);
        });
    }

    override load(dataObj: Record<string, any>, settings: LoadSettings) {
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        if (this.shape?.graphicType !== dataObj.graphic) {
            this._createShape(dataObj.graphic);
        }
        super.load(dataObj, settings);

        this.autoUpdate = initAutoUpdate;

        return this;
    }
}
